/* iter-studio-dashboard-3 — Teleplay QA admin panel.
 *
 * Lets a reviewer trigger a REAL teleplay conversion against a
 * user's project, then browse past samples with fountain preview,
 * reviewer notes, and a 1-5 rating.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Tv, Play, Loader2, Star, CheckCircle2, XCircle, Trash2, Save,
} from 'lucide-react';
import { openConfirm } from '@/components/admin/ConfirmDialog';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function StarRating({ value, onChange, testId }) {
  return (
    <div className="flex gap-0.5" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(value === n ? 0 : n)}
          className={n <= (value || 0) ? 'text-amber-500' : 'text-neutral-300 hover:text-amber-300'}
          data-testid={`${testId}-star-${n}`}
          aria-label={`Rate ${n} stars`}
        >
          <Star className="h-3.5 w-3.5" fill={n <= (value || 0) ? 'currentColor' : 'none'} />
        </button>
      ))}
    </div>
  );
}

function SignalChip({ ok, children, testId }) {
  return (
    <span
      className={
        'inline-flex items-center gap-0.5 rounded-sm border px-1.5 py-0.5 text-[10px] '
        + (ok
          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200'
          : 'border-rose-500/40 bg-rose-500/10 text-rose-800 dark:text-rose-200')
      }
      data-testid={testId}
    >
      {ok ? <CheckCircle2 className="h-2.5 w-2.5" /> : <XCircle className="h-2.5 w-2.5" />}
      {children}
    </span>
  );
}

export default function TeleplayQAPanel() {
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectId, setProjectId] = useState('');
  const [style, setStyle] = useState('show');
  const [maxCh, setMaxCh] = useState(6);
  const [generating, setGenerating] = useState(false);
  const [activeSample, setActiveSample] = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [reviewRating, setReviewRating] = useState(0);
  const [savingReview, setSavingReview] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/teleplay-qa`);
      setSamples(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load samples.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!projectId.trim()) return toast.error('Enter a project id to convert.');
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/admin/teleplay-qa/generate`, {
        project_id:      projectId.trim(),
        narrative_style: style,
        max_chapters:    Number(maxCh) || 6,
      });
      toast.success(`Sample generated · ${r.data.id}`);
      await load();
      // Immediately load the fresh fountain for review.
      const full = await axios.get(`${API}/admin/teleplay-qa/${r.data.id}`);
      setActiveSample(full.data);
      setReviewNote('');
      setReviewRating(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed.');
    } finally { setGenerating(false); }
  };

  const openSample = async (id) => {
    try {
      const r = await axios.get(`${API}/admin/teleplay-qa/${id}`);
      setActiveSample(r.data);
      setReviewNote(r.data.reviewer_note || '');
      setReviewRating(r.data.rating || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open sample.');
    }
  };

  const saveReview = async () => {
    if (!activeSample) return;
    setSavingReview(true);
    try {
      const r = await axios.put(`${API}/admin/teleplay-qa/${activeSample.id}`, {
        reviewer_note: reviewNote,
        rating:        reviewRating,
      });
      setActiveSample(r.data);
      toast.success('Review saved.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSavingReview(false); }
  };

  const deleteSample = async (id) => {
    if (!(await openConfirm({
      title: 'Delete this teleplay QA sample?',
      tone: 'destructive',
    }))) return;
    try {
      await axios.delete(`${API}/admin/teleplay-qa/${id}`);
      if (activeSample?.id === id) setActiveSample(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4" data-testid="admin-teleplay-qa-panel">
      <div className="flex items-start gap-2">
        <Tv className="mt-0.5 h-5 w-5 text-primary" />
        <div>
          <h2 className="text-[18px] font-semibold"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Teleplay QA · full-manuscript samples
          </h2>
          <p className="text-[11.5px] text-muted-foreground">
            Run a real teleplay conversion against any project id and stash the
            fountain here so reviewers can spot-check TEASER + ACT-OUT structure
            before you promote the format to authors.
          </p>
        </div>
      </div>

      {/* Generate form */}
      <div className="grid gap-3 rounded-md border border-border bg-card p-3 md:grid-cols-[2fr_1fr_1fr_auto]"
           data-testid="admin-teleplay-qa-generate">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Project id
          </Label>
          <Input value={projectId} onChange={(e) => setProjectId(e.target.value)}
                 placeholder="proj_…" data-testid="admin-teleplay-qa-pid" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Narrative style
          </Label>
          <select value={style} onChange={(e) => setStyle(e.target.value)}
                  data-testid="admin-teleplay-qa-style"
                  className="mt-1 h-9 w-full rounded-sm border border-border bg-background px-2 text-[12.5px]">
            <option value="show">Show</option>
            <option value="tell">Tell</option>
            <option value="auto">Auto</option>
          </select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Max chapters
          </Label>
          <Input type="number" min={1} max={40} value={maxCh}
                 onChange={(e) => setMaxCh(e.target.value)}
                 data-testid="admin-teleplay-qa-max" />
        </div>
        <Button
          onClick={generate}
          disabled={generating}
          className="mt-auto gap-1"
          data-testid="admin-teleplay-qa-run"
        >
          {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
          Generate
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-[1fr_1.4fr]">
        {/* Sample list */}
        <div className="rounded-md border border-border bg-card">
          <div className="border-b border-border px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground">
            Recent samples {loading ? '· loading…' : `· ${samples.length}`}
          </div>
          <div className="max-h-[520px] overflow-y-auto" data-testid="admin-teleplay-qa-list">
            {loading && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {!loading && samples.length === 0 && (
              <p className="p-4 text-[11.5px] text-muted-foreground"
                 data-testid="admin-teleplay-qa-empty">
                No samples yet. Generate one above.
              </p>
            )}
            {samples.map((s) => (
              <div key={s.id}
                   className={
                     'flex flex-col gap-1 border-b border-border px-3 py-2 text-[11.5px] cursor-pointer transition '
                     + (activeSample?.id === s.id ? 'bg-primary/5' : 'hover:bg-secondary')
                   }
                   onClick={() => openSample(s.id)}
                   data-testid={`admin-teleplay-qa-row-${s.id}`}>
                <div className="flex items-center gap-1.5">
                  <span className="truncate font-semibold text-foreground">
                    {s.project_title || s.project_id}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    · {s.chapters_used}ch · {s.signals?.scene_count || 0} scenes
                  </span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); deleteSample(s.id); }}
                    className="ml-auto rounded-sm p-1 text-neutral-400 hover:bg-rose-500/10 hover:text-rose-600"
                    data-testid={`admin-teleplay-qa-del-${s.id}`}
                    title="Delete sample"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-1">
                  <SignalChip ok={s.signals?.has_teaser}   testId={`sig-teaser-${s.id}`}>TEASER</SignalChip>
                  <SignalChip ok={s.signals?.has_act_one}  testId={`sig-actone-${s.id}`}>ACT ONE</SignalChip>
                  <SignalChip ok={s.signals?.has_act_out}  testId={`sig-actout-${s.id}`}>ACT-OUT</SignalChip>
                  <StarRating value={s.rating || 0} onChange={() => { /* readonly here */ }}
                              testId={`rating-${s.id}`} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Active sample viewer */}
        <div className="rounded-md border border-border bg-card">
          <div className="border-b border-border px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground">
            {activeSample ? `Sample · ${activeSample.id}` : 'Select a sample to review'}
          </div>
          {activeSample ? (
            <div className="p-3">
              <div className="mb-2 flex items-center gap-2 text-[11.5px] text-muted-foreground">
                <span className="font-semibold text-foreground">
                  {activeSample.project_title || activeSample.project_id}
                </span>
                <span>· {activeSample.genre || 'no-genre'}</span>
                <span>· {activeSample.narrative_style} style</span>
                <span>· {activeSample.signals?.scene_count || 0} scenes</span>
              </div>
              <pre
                className="max-h-[300px] overflow-auto whitespace-pre-wrap rounded-sm border border-border bg-background p-2 text-[11px] leading-relaxed"
                data-testid="admin-teleplay-qa-fountain"
              >
                {activeSample.fountain}
              </pre>

              <div className="mt-3">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Reviewer notes
                </Label>
                <Textarea
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  rows={3}
                  placeholder="Does the TEASER hook? Are ACT-OUT beats real cliffhangers? Cast names consistent?"
                  data-testid="admin-teleplay-qa-note"
                />
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Rating
                </Label>
                <StarRating value={reviewRating} onChange={setReviewRating}
                            testId="admin-teleplay-qa-rating" />
                <Button
                  onClick={saveReview}
                  disabled={savingReview}
                  className="ml-auto h-7 gap-1"
                  data-testid="admin-teleplay-qa-save"
                >
                  {savingReview ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                  Save review
                </Button>
              </div>
            </div>
          ) : (
            <p className="p-4 text-[11.5px] text-muted-foreground"
               data-testid="admin-teleplay-qa-none-selected">
              Pick a sample from the list, or generate a fresh one to review.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
